import { ArrowUp, AudioLines } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { WhisperSpeechToText } from './WhisperSpeechToText';

interface SendControlsProps {
  onSend: () => void;
  onStop?: () => void;
  onOpenVoiceMode?: () => void;
  inputValue: string;
  isLoading: boolean;
  canSendMessage: boolean;
  isDarkMode: boolean;
  onSpeechTranscript: (transcript: string) => void;
  onListeningChange?: (isListening: boolean) => void;
}

export const SendControls = ({
  onSend,
  onStop,
  onOpenVoiceMode,
  inputValue,
  isLoading,
  canSendMessage,
  isDarkMode,
  onSpeechTranscript,
  onListeningChange
}: SendControlsProps) => {
  const hasText = Boolean(inputValue.trim());

  const activeButtonClassName = isDarkMode
    ? 'bg-white text-black hover:bg-white/90'
    : 'hover:bg-black/80 bg-[#1c1c1c] text-gray-500';

  const inactiveButtonClassName = isDarkMode
    ? 'bg-[#505050] text-black'
    : 'bg-[#505050] text-gray-500';

  return (
    <div className={`xs:pr-2 pr-0 py-1 flex items-center rounded-md w-full flex flex-row items-end gap-2`}>
      <div className="w-full">
        <WhisperSpeechToText
          onTranscript={onSpeechTranscript}
          isDarkMode={isDarkMode}
          onListeningChange={onListeningChange}
        />
      </div>

      <div className="flex items-center gap-2">
        {isLoading && onStop ? (
          <Button
            onClick={onStop}
            className="h-8 w-8 rounded-full p-0 bg-red-500 hover:bg-red-600 text-white shadow-md"
          >
            <div className="h-3 w-3 bg-current rounded-sm"></div>
          </Button>
        ) : hasText ? (
          <Button
            onClick={onSend}
            disabled={!hasText || isLoading || !canSendMessage}
            className={`h-8 w-8 rounded-full border-none p-0 transition-transform duration-200 ease-out ${!hasText || isLoading || !canSendMessage
              ? inactiveButtonClassName
              : activeButtonClassName
              }`}
          >
            <ArrowUp className={`w-4 h-4 ${isDarkMode ? 'text-black' : 'text-white'}`} />
          </Button>
        ) : (
          <div className="relative group">
            <Button
              type="button"
              onClick={onOpenVoiceMode}
              disabled={isLoading || !onOpenVoiceMode}
              className={`h-8 w-8 rounded-full border-none p-0 transition-transform duration-200 ease-out ${activeButtonClassName}`}
            >
              <AudioLines className={`w-5 h-5 ${isDarkMode ? 'text-black' : 'text-white'}`} />
            </Button>
            <div className="absolute right-0 bottom-full mb-2 px-2 py-1 text-xs rounded-md bg-black/80 text-white whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
              Voice Mode
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
